import { Fragment } from 'react';
import {
    ActivableRenderer,
    ActivableRendererProps,
    IconButton,
    IconName,
    LoadingOverlay,
    Portal,
    Size,
    Spinner,
    useScrollable,
} from '@optimus-web/core';
import {
    SidePanelAction,
    SidePanelContainer,
    SidePanelContent,
    SidePanelHeader,
    SidePanelHeaderAction,
    SidePanelHeaderCloseContainer,
    SidePanelHeaderSubtitle,
    SidePanelHeaderTitle,
    SidePanelHeaderTitleWrapper,
    SidePanelWrapper,
    TitleTagContainer,
} from './style';

export interface SidePanelProps extends ActivableRendererProps {
    onClose: () => void;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    tag?: React.ReactNode;
    content: React.ReactNode;
    actions?: React.ReactNode;
    opened: boolean;
    portalContainerId?: string;
    headerActions: React.ReactNode;
    width: Size;
    loading?: boolean;
    /**
     * Workaround for react hook form content. Since Portal is used here actual position
     * of form fields is not inside of <form> (and <FormProvider/>) element which is used to wrap
     * <SidePanel /> component.
     */
    Wrapper?: React.FC<React.PropsWithChildren<{}>>;
}

const SidePanelActivatorFunction = ActivableRenderer<React.Component<SidePanelProps>, SidePanelProps>({
    activePropName: 'opened',
});

const SidePanelComponent = ({
    onClose,
    title,
    subtitle,
    tag,
    content,
    actions,
    opened,
    portalContainerId,
    headerActions,
    width,
    loading,
    Wrapper = Fragment,
    ...other
}: SidePanelProps) => {
    const { hasScrollBar, contentRef, scrollStart, scrollEnd } = useScrollable();

    return (
        <Portal containerId={portalContainerId} {...other}>
            <Wrapper>
                <SidePanelWrapper>
                    <SidePanelContainer opened={opened} width={width} data-test-sidepanel>
                        <SidePanelHeader hasScrollBar={hasScrollBar && !scrollStart}>
                            <SidePanelHeaderCloseContainer>
                                <IconButton
                                    variant="tertiary"
                                    size="small"
                                    icon={IconName.CrossClose}
                                    onClick={onClose}
                                    data-test-id="close-panel"
                                    type="submit"
                                />
                            </SidePanelHeaderCloseContainer>
                            <SidePanelHeaderTitleWrapper vertical>
                                <SidePanelHeaderTitle itemAlignment="start" spacing="150">
                                    {title}
                                    <TitleTagContainer>{tag}</TitleTagContainer>
                                </SidePanelHeaderTitle>
                                <SidePanelHeaderSubtitle>{subtitle}</SidePanelHeaderSubtitle>
                            </SidePanelHeaderTitleWrapper>
                            <SidePanelHeaderAction>{headerActions}</SidePanelHeaderAction>
                        </SidePanelHeader>
                        <SidePanelContent ref={contentRef}>{content}</SidePanelContent>
                        <LoadingOverlay show={loading}>
                            <Spinner />
                        </LoadingOverlay>
                        {actions ? (
                            <SidePanelAction hasScrollBar={hasScrollBar && !scrollEnd}>{actions}</SidePanelAction>
                        ) : null}
                    </SidePanelContainer>
                </SidePanelWrapper>
            </Wrapper>
        </Portal>
    );
};

export const SidePanel = SidePanelActivatorFunction(SidePanelComponent);
